import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronRight, BookOpen, GraduationCap, BarChart3, Shield, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/context/auth-context";
import { Course } from "@/pages/admin/types";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatCEU } from "@/lib/utils";
import { PageHeader } from "./admin/components/PageHeader";

const Index = () => {
  const { profile, user } = useAuth();
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin';
  const isAuthenticated = !!user;

  // Fetch course metrics for the admin dashboard card
  const { data: courseMetrics } = useQuery({
    queryKey: ['course-metrics'],
    queryFn: async () => {
      if (!isStaff) return null;
      
      const { data: courses, error } = await supabase
        .from('courses')
        .select('id, status, start_date')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching course metrics:", error);
        return null;
      }

      const published = courses.filter(c => c.status === 'published').length;
      const upcoming = courses.filter(c => 
        c.status === 'published' && 
        c.start_date && 
        new Date(c.start_date) > new Date()
      ).length;
      
      const total = courses.length;
      
      return { published, upcoming, total };
    },
    enabled: isStaff,
  });

  // Fetch a single upcoming course for the preview card (for non-staff users)
  const { data: upcomingCourse } = useQuery({
    queryKey: ['upcoming-course-preview'],
    queryFn: async () => {
      if (isStaff) return null;
      
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('courses')
        .select('title, hours, event_type, start_date')
        .eq('status', 'published')
        .gte('start_date', today)
        .order('start_date', { ascending: true })
        .limit(1)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error("Error fetching upcoming course:", error);
        return null;
      }
      
      return data || null;
    },
    enabled: !isStaff,
  });

  return (
    <div className="w-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-accent-light/10 to-accent-subtle p-4 pt-6 pb-16 min-h-[calc(100vh-8rem-4rem)]">
      <PageHeader
        icon={<BookOpen className="h-4 w-4 text-white" />} 
        title="Welcome to NWSISD CEU Portal"
        subtitle="Your gateway to professional development and continuing education."
      />
      {/* Lattice design element */}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSIjMDA0MTYxIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQwem0zMSAyMmgtNHY0aDR2LTR6bS01LTVoLTR2NGg0di00em0tNSA1aC00djRoNHYtNHptLTUtNWgtNHY0aDR2LTR6Ii8+PC9nPjwvc3ZnPg==')]"></div>
      
      <div className="text-center max-w-3xl mx-auto relative z-10">
        <img 
          src="/lovable-uploads/78ce5b8a-a3f5-4ace-b6b5-c26755a86e21.png"
          alt="NWSISD Logo"
          className="h-32 w-auto mx-auto mb-10 drop-shadow-sm animate-fade-in"
        />
        
        <h1 className="text-4xl font-title font-bold mb-4 text-primary animate-slide-up">
          Welcome to NWSISD CEU Portal
        </h1>
        
        <p className="text-xl font-subtitle text-muted-foreground mb-10 animate-slide-up delay-100 max-w-xl mx-auto">
          Your gateway to professional development and continuing education.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 animate-slide-up delay-200">
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-md border border-accent-light/20 hover:shadow-lg transition-all hover:-translate-y-1 text-center">
            <div className="bg-primary/10 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-7 w-7 text-primary" />
            </div>
            <h3 className="font-heading font-bold text-primary mb-2">Browse Courses</h3>
            <p className="font-serif text-muted-foreground text-sm mb-4">Explore professional development opportunities</p>
            {isAuthenticated ? (
              <Button asChild variant="ghost" className="font-section text-accent">
                <Link to="/courses">
                  View Courses
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="ghost" className="font-section text-accent">
                <Link to="/auth/login">
                  Sign In to View Courses
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
          
          {isStaff ? (
            <div className="bg-gradient-to-br from-primary/90 to-primary/80 backdrop-blur-sm rounded-lg p-6 shadow-md border border-primary/20 hover:shadow-lg transition-all hover:-translate-y-1 text-center">
              <div className="bg-white/10 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-7 w-7 text-white" />
              </div>
              <h3 className="font-heading font-bold text-white mb-2">Course Dashboard</h3>
              
              {courseMetrics ? (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-white/10 rounded p-2">
                    <p className="font-title text-xl font-bold text-white">{courseMetrics.total}</p>
                    <p className="font-caption text-xs text-white/70">Total</p>
                  </div>
                  <div className="bg-white/10 rounded p-2">
                    <p className="font-title text-xl font-bold text-white">{courseMetrics.published}</p>
                    <p className="font-caption text-xs text-white/70">Active</p>
                  </div>
                  <div className="bg-white/10 rounded p-2">
                    <p className="font-title text-xl font-bold text-white">{courseMetrics.upcoming}</p>
                    <p className="font-caption text-xs text-white/70">Upcoming</p>
                  </div>
                </div>
              ) : (
                <p className="font-serif text-white/80 text-sm mb-4">Loading course metrics...</p>
              )}
              
              <Button asChild variant="secondary" className="font-section text-primary w-full">
                <Link to="/admin/courses">
                  Manage Courses
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-md border border-accent-light/20 hover:shadow-lg transition-all hover:-translate-y-1 text-center">
              <div className="bg-primary/10 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-primary mb-2">Upcoming Courses Preview</h3>
              <p className="font-section text-muted-foreground text-sm mb-4">
                {upcomingCourse ? (
                  <>
                    <span className="font-section">{upcomingCourse.title}</span>
                    <br />
                    <span className="font-section">{formatCEU(upcomingCourse.hours)} CEUs</span>
                    {upcomingCourse.start_date && (
                      <>
                        <br />
                        <span className="font-section text-xs text-muted-foreground">
                          {new Date(upcomingCourse.start_date).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <span className="font-section">No upcoming courses</span>
                    <br />
                    <span className="font-section">Sign in to view more!</span>
                  </>
                )}
              </p>
              {isAuthenticated ? (
                <Button asChild variant="ghost" className="font-section text-accent">
                  <Link to="/schedule">
                    View Schedule
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <Button asChild variant="ghost" className="font-section text-accent">
                  <Link to="/auth/login">
                    Sign In to View
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          )}
          
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-md border border-accent-light/20 hover:shadow-lg transition-all hover:-translate-y-1 text-center">
            <div className="bg-secondary/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="h-7 w-7 text-secondary" />
            </div>
            <h3 className="font-heading font-bold text-primary mb-2">Your Progress</h3>
            <p className="font-serif text-muted-foreground text-sm mb-4">Track your certificates and achievements</p>
            {isAuthenticated ? (
              <Button asChild variant="ghost" className="font-section text-accent">
                <Link to="/certificates">
                  View Certificates
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="ghost" className="font-section text-accent">
                <Link to="/auth/login">
                  Sign In to View
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            )}
          </div>
        </div>
        
        <div className="max-w-md mx-auto animate-slide-up delay-300">
          <div className="p-4 bg-accent-subtle rounded-lg border border-accent-light/20 mb-6">
            <p className="font-quote italic text-primary text-sm">
              "Fostering inclusive educational environments through professional development and cultural competency."
            </p>
          </div>
          
          {isAuthenticated ? (
            <Button asChild className="w-full font-section text-base py-6">
              <Link to="/courses">
                Browse Available Courses
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          ) : (
            <Button asChild className="w-full font-section text-base py-6">
              <Link to="/auth/login">
                Sign In to Get Started
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          )}
        </div>
        
        {/* Important Policy Links */}
        <div className="flex items-center justify-center gap-6 mt-12 text-sm">
          <Link to="/privacy-policy" className="flex items-center text-primary hover:underline">
            <Shield className="h-4 w-4 mr-1" /> 
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="flex items-center text-primary hover:underline">
            <FileText className="h-4 w-4 mr-1" /> 
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Index;
